import {
  useIsDesktop,
  WSAlert,
  WSButton,
  WSDataItem,
  WSFlexBox,
  WSGrid,
  WSIcon,
  WSList,
  WSPage,
  WSPageToolbar,
  WSPanel,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  Batch,
  BatchStatus,
  BatchSummaryExpand,
  CustomFieldResourceType,
  PayeeImportBatchConfiguration
} from "@wingspanhq/wingspan-api-sdk";
import {
  FlatfileProvider,
  Space,
  useFlatfile,
  Workbook
} from "@flatfile/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { CSVLink } from "react-csv";

import {
  queriesBatches,
  queriesCustomFields,
  queriesEngagements
} from "@query";
import { WSQueryCache } from "@ws-react-query";
import { ErrorAlert } from "@components/ErrorAlert";
import { useAccountAccess } from "@hooks/useAccountAccess";
import { WSQueries } from "../../../../query/WSQuery";
import {
  IS_DEV_ENV,
  IS_PRODUCTION_ENV
} from "../../../../shared/constants/environment";
import { useBrowserPageTitle } from "../../../../shared/hooks/useBrowserPageTitle";
import { themeConfig } from "../../../../shared/flatfile/themeConfig";
import { flatfileProviderConfig } from "../../utils/flatfileThemeConfig";
import { useDownloadContractorExampleTemplate } from "../../components/DownloadContractorExampleTemplateButton";
import { ImportBatchEventHistoryWidget } from "../../components/ImportBatchEventHistoryWidget";
import { ImportBatchStatusAlert } from "../../components/ImportBatchStatusAlert";
import { ImportBatchSteps } from "../../components/ImportBatchSteps";
import { useModalEditImportBatchName } from "../../components/ModalEditImportBatchName";
import { useImportBatchActions } from "../../hooks/useImportBatchActions";
import {
  useGoToImportContractorsBatches,
  routeHelpersImportContractorsBatch,
  routeHelpersImportContractorsBatchHistory
} from "../../paths";
import { selectorImportBatchAge } from "../../selectors/selectorImportBatchAge";
import { selectorImportBatchNames } from "../../selectors/selectorImportBatchNames";
import { selectorIsOldImportBatch } from "../../selectors/selectorIsOldImportBatch";
import {
  CONTRACTOR_BATCH_DELETE_DESCRIPTION,
  CONTRACTOR_BATCH_DELETE_EMPTY_DESCRIPTION,
  CONTRACTOR_ENTITY_LABEL,
  CONTRACTOR_ENTITY_LABEL_PLURAL,
  CONTRACTOR_ENTITY_LABEL_PLURAL_LOWER
} from "./constants";
import { ContractorBatchDetailsWidget } from "./ContractorBatchDetailsWidget";
import { ContractorBatchStatusWidget } from "./ContractorBatchStatusWidget";
import { buildContractorImportSheetConfig } from "./contractorImportSheet";
import { useContractorImportUpload } from "./useContractorImportUpload";
import { useDownloadContractorImportErrorsCSV } from "./useDownloadContractorImportErrorsCSV";

import styles from "./styles.module.scss";

const ContractorBatchDetails: React.FC<{
  batch: Batch;
  openPortalOnMount?: boolean;
}> = ({ batch, openPortalOnMount }) => {
  const batchId = batch.id as string;
  const history = useHistory();
  const location = useLocation();
  const isDesktop = useIsDesktop();
  const [showOldBatchAlert, setShowOldBatchAlert] = useState(true);
  const [uploadError, setUploadError] = useState<unknown>(undefined);
  const exampleTemplateCSVLinkRef = useRef<any>(null);
  const errorsCSVLinkRef = useRef<any>(null);

  const { isImpersonatedByWingspanAdmin } = useAccountAccess();
  const goToContractorBatches = useGoToImportContractorsBatches();
  const goToHistory = routeHelpersImportContractorsBatchHistory.useGoTo();
  const modalEditName = useModalEditImportBatchName();

  const querySummary = queriesBatches.getBatchSummary.useQuery(
    { batchId, expand: [BatchSummaryExpand.BREAKDOWN] },
    { refetchOnMount: true }
  );

  const queryCustomFields = queriesCustomFields.listCustomFields.useQueryAll({
    filter: { resourceType: { eq: CustomFieldResourceType.PAYEE } }
  });

  const configuration = batch._configuration as
    | PayeeImportBatchConfiguration
    | undefined;
  const queryEngagement = queriesEngagements.getEngagement.useQuery(
    { engagementId: configuration?.engagementId as string },
    { enabled: !!configuration?.engagementId }
  );

  const submitRows = useContractorImportUpload(batchId);
  const { csvProps: exampleTemplateCSVProps } =
    useDownloadContractorExampleTemplate();
  const { errorsCSVFileProps } = useDownloadContractorImportErrorsCSV(batchId);

  // The upload posts items with `batchesApi.addBatchItem` directly rather than
  // through a registry mutation, so nothing invalidates the sibling reads —
  // the failed-item list behind "Download failed imports" would keep serving
  // its pre-upload cache, and the batches list its pre-upload counts.
  const refreshAfterUpload = () =>
    Promise.all([
      querySummary.refetch(),
      WSQueryCache.invalidateQueries(queriesBatches.listBatches.queryKeyBase),
      WSQueryCache.invalidateQueries(
        queriesBatches.listBatchItems.queryKeyBase
      ),
      WSQueryCache.invalidateQueries(queriesBatches.getBatch.queryKeyBase)
    ]);

  const { openPortal, closePortal } = useFlatfile({
    onClose: () => {
      refreshAfterUpload();
    }
  });

  const downloadExampleCSV = () => {
    exampleTemplateCSVLinkRef.current?.link?.click();
  };
  const downloadErrorsCSV = () => {
    errorsCSVLinkRef.current?.link?.click();
  };

  // No listener claims this space — the browser owns the upload — so the
  // metadata carries only what the portal itself renders.
  const spaceMetadata = useMemo(
    () => ({
      batchId,
      sidebarConfig: {
        showSidebar: isImpersonatedByWingspanAdmin || IS_DEV_ENV
      },
      ...themeConfig
    }),
    [batchId, isImpersonatedByWingspanAdmin]
  );

  // Custom fields become blueprint columns, so opening the portal before they
  // land would show a sheet the account's required columns are missing from.
  const isBlueprintReady = !queryCustomFields.isLoading;

  // The start screen hands off with `showBatchCreatedModal` once the batch has
  // been created, so the upload is the next thing the user needs.
  const hasAutoOpened = useRef(false);
  useEffect(() => {
    if (openPortalOnMount && isBlueprintReady && !hasAutoOpened.current) {
      hasAutoOpened.current = true;
      // Drop the hand-off flag from history state, or every reload and every
      // back-navigation to this batch reopens the portal.
      history.replace(location.pathname);
      openPortal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBlueprintReady]);

  const progress = querySummary.data?.progress;
  const itemCount = progress?.total;
  const isEmptyBatch = itemCount === 0;
  const notes = batch.notes;

  const { batchName, batchFileName } = selectorImportBatchNames({
    batch,
    entityLabel: CONTRACTOR_ENTITY_LABEL
  });

  const actions = useImportBatchActions({
    batch,
    isEmptyBatch,
    failedCount: progress?.failed,
    entityLabel: CONTRACTOR_ENTITY_LABEL,
    entityLabelPlural: CONTRACTOR_ENTITY_LABEL_PLURAL_LOWER,
    deleteDescription: CONTRACTOR_BATCH_DELETE_DESCRIPTION,
    deleteEmptyBatchDescription: CONTRACTOR_BATCH_DELETE_EMPTY_DESCRIPTION,
    downloadExampleCSV,
    downloadErrorsCSV,
    onDeleted: () => goToContractorBatches()
  });
  // The failed-imports download belongs to the status widget, not the toolbar.
  const toolbarActions = useMemo(
    () => actions.filter(action => action.name !== "downloadErrorsCSV"),
    [actions]
  );

  const onEditName = () => modalEditName.open({ batch });

  const eventHistoryProps = {
    batch,
    itemCount,
    completedCount: progress?.completed,
    failedCount: progress?.failed,
    entityLabel: CONTRACTOR_ENTITY_LABEL,
    entityLabelPlural: CONTRACTOR_ENTITY_LABEL_PLURAL,
    onSeeMore: () => goToHistory({ batchId })
  };

  return (
    <WSPage>
      <Space config={{ name: "Contractors Upload", metadata: spaceMetadata }}>
        <Workbook
          config={{
            name: "Contractors upload",
            sheets: [
              buildContractorImportSheetConfig(queryCustomFields.flatData ?? [])
            ]
          }}
          onSubmit={async ({ sheet }) => {
            setUploadError(undefined);
            try {
              // Only rows that passed the blueprint's own validation — the
              // rest stay in the portal for the user to correct.
              const records = await sheet.validData();
              const result = await submitRows(
                (records?.records ?? []).map(
                  (record: { values: Record<string, { value: unknown }> }) =>
                    Object.fromEntries(
                      Object.entries(record.values).map(([key, cell]) => [
                        key,
                        cell?.value
                      ])
                    )
                )
              );
              await refreshAfterUpload();
              if (result.rejected.length === 0) {
                closePortal();
              } else {
                setUploadError(
                  new Error(
                    `${result.submitted} rows uploaded, ${result.rejected.length} were rejected. Fix them in your spreadsheet and upload again.`
                  )
                );
              }
            } catch (error) {
              setUploadError(error);
            }
          }}
        />
      </Space>

      <CSVLink
        ref={exampleTemplateCSVLinkRef}
        className={styles.hiddenButton}
        {...exampleTemplateCSVProps}
      />
      <CSVLink
        ref={errorsCSVLinkRef}
        className={styles.hiddenButton}
        {...errorsCSVFileProps}
      />

      {/*
        Every widget below reads the row counts, and an unknown count is not the
        same as zero: a batch with no rows yet would read as "upload finished"
        for the first frame and offer "Import contractors" before flipping to
        the empty state. Hold the page until the summary lands, as the v1 route
        did. The Flatfile `Space` stays outside so the portal can still be
        opened on arrival from the start screen.
      */}
      {/*
        The custom fields become blueprint columns, so the page waits on them
        too: every action that opens the portal is rendered below this gate,
        and an early open would show a sheet the account's own columns are
        missing from.
      */}
      <WSQueries queries={{ querySummary, queryCustomFields }}>
        {({ querySummaryData: batchSummary }) => (
          <>
            <WSPageToolbar
              breadcrumbs={[
                {
                  label: "Bulk imports (contractors)",
                  onClick: () => goToContractorBatches()
                },
                { label: batchName }
              ]}
              actions={toolbarActions}
            />

            <WSFlexBox.CenterY mb="S">
              <WSText.Heading3 weight="medium" mr="L">
                {batchName}
              </WSText.Heading3>
              <WSButton.Link onClick={onEditName}>Edit name</WSButton.Link>
            </WSFlexBox.CenterY>

            {batchFileName && (
              <WSFlexBox.CenterY>
                <WSIcon name="file" mr="XS" size="XS" />
                <WSText.ParagraphXs weight="book" color="gray600">
                  {batchFileName}
                </WSText.ParagraphXs>
              </WSFlexBox.CenterY>
            )}

            <WSGrid gutter="XL" my="2XL">
              <WSGrid.Item span={{ s: "12", m: "8" }}>
                <WSList>
                  {!isDesktop && (
                    <ImportBatchSteps
                      batch={batch}
                      isEmptyBatch={isEmptyBatch}
                      failedCount={progress?.failed}
                    />
                  )}

                  <ErrorAlert
                    context="BulkBatchImportError"
                    error={uploadError}
                  />

                  {notes && (
                    <WSPanel>
                      <WSDataItem
                        label={{
                          text: "Notes",
                          action: { label: "Edit", onClick: onEditName }
                        }}
                        value={notes}
                      />
                    </WSPanel>
                  )}

                  {showOldBatchAlert &&
                    batch.status === BatchStatus.CREATED &&
                    selectorIsOldImportBatch(batch) && (
                      <WSAlert
                        theme="info"
                        icon="alert-circle"
                        title={`This upload is over ${selectorImportBatchAge(
                          batch
                        )} old`}
                        onDismiss={() => setShowOldBatchAlert(false)}
                      >
                        This batch contains an upload over{" "}
                        {selectorImportBatchAge(batch)} old that was not yet
                        imported and may contain old validation data from the
                        time it was uploaded. To ensure up-to-date validation
                        for your formatted spreadsheet, delete this batch and
                        start a new one to upload and import your spreadsheet.
                        We recommend completing the import as soon as your
                        spreadsheet is finished uploading so it doesn’t sit for
                        an extended period of time.
                      </WSAlert>
                    )}

                  <ImportBatchStatusAlert
                    batch={batch}
                    itemCount={itemCount}
                    completedCount={progress?.completed}
                    failedCount={progress?.failed}
                  />

                  <ContractorBatchStatusWidget
                    batch={batch}
                    batchSummary={batchSummary}
                    downloadExampleCSV={downloadExampleCSV}
                    downloadErrorsCSV={downloadErrorsCSV}
                    onDeleted={() => goToContractorBatches()}
                  />

                  <ContractorBatchDetailsWidget
                    batch={batch}
                    engagement={queryEngagement.data}
                  />

                  {!isDesktop && (
                    <ImportBatchEventHistoryWidget {...eventHistoryProps} />
                  )}
                </WSList>
              </WSGrid.Item>

              <WSGrid.Item span={{ s: "12", m: "4" }}>
                {isDesktop && (
                  <WSList gap="L">
                    <ImportBatchSteps
                      batch={batch}
                      isEmptyBatch={isEmptyBatch}
                      failedCount={progress?.failed}
                    />
                    <ImportBatchEventHistoryWidget {...eventHistoryProps} />
                  </WSList>
                )}
              </WSGrid.Item>
            </WSGrid>
          </>
        )}
      </WSQueries>
    </WSPage>
  );
};

export const RouteImportContractorsBatchDetails: React.FC = () => {
  useBrowserPageTitle("Bulk imports: Contractor batch");
  const { params, state } = routeHelpersImportContractorsBatch.useData();
  const queryBatch = queriesBatches.getBatch.useQuery(
    { batchId: params.batchId },
    { refetchOnMount: true }
  );

  return (
    <FlatfileProvider
      publishableKey={process.env.REACT_APP_FLATFILE_PUBLISHABLE_KEY as string}
      config={{ debug: !IS_PRODUCTION_ENV, ...flatfileProviderConfig }}
    >
      <WSQueries queries={{ queryBatch }}>
        {({ queryBatchData: batch }) => (
          <ContractorBatchDetails
            batch={batch}
            openPortalOnMount={state?.showBatchCreatedModal}
          />
        )}
      </WSQueries>
    </FlatfileProvider>
  );
};
